'use client'

import { Clock16, LocationPin16, Mailbox16, X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  Navigation,
  Radio,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { compact } from 'lodash'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { useFetch, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  getAvailablePickupPoints,
  getDeliveryOptionChangeRequestDetails,
  transformAvailablePickupPointDto,
  AvailablePickupPointModel,
  updateDeliveryOptionChangeRequest,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { CoordinatesModel } from '@marketplace-web/shipping/checkout-shipping-data'
import { useLoadGoogleApi } from '@marketplace-web/shipping/google-maps-feature'
import { geocodeAddress } from '@marketplace-web/shipping/google-places-feature'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  getDefaultShippingAddress,
  getFormattedAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'

import GenericErrorModal from '../../GenericErrorModal'
import { getMatchingCarrierPoints } from './utils'
import { DeliveryOptionChangeRequestStatus } from '../../../constants'

type Props = {
  shipmentId?: number | null
  shippingOrderId: number | null | undefined
  isOpen: boolean
  onClose: () => void
}

const AcceptChangeDeliveryOptionRequestModal = ({
  shipmentId,
  shippingOrderId,
  isOpen,
  onClose,
}: Props) => {
  const translate = useTranslate('conversation.select_new_pickup_point.modal')

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [dataLoadingError, setDataLoadingError] = useState(false)
  const [availablePickupPoints, setAvailablePickupPoints] = useState<
    AvailablePickupPointModel | undefined
  >()
  const [selectedShippingPoint, setSelectedShippingPoint] = useState<string | undefined>()
  const [addressCoordinates, setAddressCoordinates] = useState<CoordinatesModel | null>(null)

  const asset = useAsset('/assets/shipping')
  const { locale } = useIntl()

  const CARRIER_ICON_PLACEHOLDER = 'carrier-placeholder.svg'

  const isGoogleApiLoaded = useLoadGoogleApi()

  const {
    isLoading: isAddressLoading,
    error: addressLoadingError,
    fetch: fetchDefaultShippingAddress,
    transformedData: defaultShippingAddress,
  } = useFetch(getDefaultShippingAddress, transformDefaultShippingAddress)

  useEffect(() => {
    if (!isOpen) return

    fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress, isOpen])

  const geocodeUserAddress = useCallback(async () => {
    if (!isOpen) return

    const formattedAddress = getFormattedAddress(defaultShippingAddress)

    if (!formattedAddress) return

    const geocodedAddress = await geocodeAddress(formattedAddress)

    if (!geocodedAddress?.formatted_address || !geocodedAddress?.geometry) return

    const geocodedAddressCoordinates = {
      latitude: geocodedAddress.geometry.location.lat(),
      longitude: geocodedAddress.geometry.location.lng(),
    }

    setAddressCoordinates(geocodedAddressCoordinates)
  }, [isOpen, defaultShippingAddress])

  useEffect(() => {
    if (!isOpen) return
    if (!defaultShippingAddress) return
    if (!isGoogleApiLoaded) return

    geocodeUserAddress()
  }, [geocodeUserAddress, isOpen, defaultShippingAddress, isGoogleApiLoaded])

  const fetchAvailablePickupPointsData = useCallback(async () => {
    if (!shipmentId || !isOpen) return

    setIsDataLoading(true)

    const requestDetailsResponse = await getDeliveryOptionChangeRequestDetails(shipmentId)

    if ('errors' in requestDetailsResponse) {
      setIsDataLoading(false)
      setDataLoadingError(true)

      return
    }

    const countryCode = defaultShippingAddress?.countryIsoCode

    if (!countryCode) return
    if (!addressCoordinates) return

    const { latitude, longitude } = addressCoordinates

    const pickupPointsResponse = await getAvailablePickupPoints({
      shippingOrderId,
      countryCode,
      latitude,
      longitude,
      shipmentId,
      rateRootUuid: requestDetailsResponse.root_rate_uuid,
    })

    if ('errors' in pickupPointsResponse) {
      setIsDataLoading(false)
      setDataLoadingError(true)

      return
    }

    const transformedAvailablePickupPoints = transformAvailablePickupPointDto(pickupPointsResponse)

    setAvailablePickupPoints(transformedAvailablePickupPoints)

    const selectedPoint = transformedAvailablePickupPoints.suggestedShippingPointCode
      ? transformedAvailablePickupPoints.suggestedShippingPointCode
      : transformedAvailablePickupPoints?.nearbyShippingPoints[0]?.point.code

    setSelectedShippingPoint(selectedPoint)

    setIsDataLoading(false)
  }, [
    shipmentId,
    isOpen,
    shippingOrderId,
    defaultShippingAddress?.countryIsoCode,
    addressCoordinates,
  ])

  useEffect(() => {
    fetchAvailablePickupPointsData()
  }, [fetchAvailablePickupPointsData])

  const handleClose = () => {
    setSelectedShippingPoint(undefined)
    setAvailablePickupPoints(undefined)
    setDataLoadingError(false)
    onClose()
  }

  const handleShippingPointSelection = (code: string) => () => {
    setSelectedShippingPoint(code)
  }

  const handleSubmit = async () => {
    setIsDataLoading(true)

    if (!shipmentId) return

    const response = await updateDeliveryOptionChangeRequest({
      shipmentId,
      status: DeliveryOptionChangeRequestStatus.Accepted,
      pickupPointCode: selectedShippingPoint,
    })

    if ('errors' in response) {
      setIsDataLoading(false)
      setDataLoadingError(true)

      return
    }
    if (response.code === ResponseCode.Ok) {
      setIsDataLoading(false)
      onClose()
    }
  }

  const renderNavigation = () => {
    return (
      <>
        <Navigation
          body={translate('modal_title')}
          right={
            <Button
              styling="flat"
              onClick={handleClose}
              iconName={X24}
              testId="choose-pickup-point-modal-close"
              inline
              aria={{ 'aria-label': translate('a11y.actions.close') }}
            />
          }
        />
        <Divider />
      </>
    )
  }

  const renderCarrierInfo = (carrierIconUrl?: string, title?: string) => {
    return (
      <Cell
        styling="tight"
        theme="transparent"
        prefix={
          <Image
            src={carrierIconUrl}
            fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
            size="regular"
            alt={title}
          />
        }
        body={
          <div className="u-flexbox u-align-items-center u-gap-medium">
            <Text
              as="h2"
              text={title}
              type="title"
              testId="choose-pickup-point-modal-carrier-title"
            />
          </div>
        }
      />
    )
  }

  const renderPrice = () => {
    const price = { amount: '0', currencyCode: 'EUR' }

    if (!price) return null

    return <Text as="h2" text={formatCurrencyAmount(price, locale)} type="title" />
  }

  const renderPickupPointSection = (
    icon: React.ComponentProps<typeof Icon>['name'],
    text: string,
  ) => (
    <div className="u-flexbox u-gap-medium">
      <Icon name={icon} color="greyscale-level-2" />
      <Text as="h3" text={text} type="subtitle" />
    </div>
  )

  const renderPickupPointInfo = (
    name: string,
    addressLine1: string,
    addressLine2: string | null,
    city: string,
    postalCode: string,
    valueProposition: string,
  ) => {
    return (
      <SeparatedList separator={<Spacer size="small" />}>
        {renderPickupPointSection(Mailbox16, name)}
        {renderPickupPointSection(
          LocationPin16,
          compact([addressLine1, addressLine2, city, postalCode]).join(', '),
        )}
        {renderPickupPointSection(Clock16, valueProposition)}
      </SeparatedList>
    )
  }

  const renderPikcupPointContent = () => {
    if (!availablePickupPoints) return null

    const { nearbyShippingOptions, nearbyShippingPoints } = availablePickupPoints

    const shippingOption = getMatchingCarrierPoints(nearbyShippingOptions, nearbyShippingPoints)

    return shippingOption.map(match => {
      const { iconUrl, name: carrierName } = match.option.lastMileCarrier
      const { valueProposition } = match.option
      const { name, addressLine1, addressLine2, code, city, postalCode } = match.point.point

      return (
        <Fragment key={code}>
          <Cell
            title={renderCarrierInfo(iconUrl, carrierName)}
            body={
              <>
                <Cell styling="tight" body={renderPrice()} theme="transparent" />
                <Spacer size="large" />
                {renderPickupPointInfo(
                  name,
                  addressLine1,
                  addressLine2 || null,
                  city,
                  postalCode,
                  valueProposition,
                )}
              </>
            }
            onClick={handleShippingPointSelection(code)}
            suffix={
              <Radio
                name={`choose-pickup-point-modal-${code}`}
                checked={selectedShippingPoint === code}
                value={code}
                onChange={() => undefined}
                testId={`choose-pickup-point-modal-radio-${code}`}
                aria={{
                  'aria-labelledby': `choose-pickup-point-modal-radio-${code}`,
                }}
              />
            }
            clickable
            testId={`choose-pickup-point-modal-cell-${code}`}
          />
          <Divider />
        </Fragment>
      )
    })
  }

  const renderSubmit = () => {
    return (
      <Cell>
        <Button
          styling="filled"
          text={translate('actions.choose_pickup_point')}
          onClick={handleSubmit}
          testId="choose-pickup-point-modal-submit-button"
        />
      </Cell>
    )
  }

  const renderEmptyState = () => {
    return (
      <Dialog show>
        {renderNavigation()}
        <Spacer size="x-large" />
        <Text
          as="h1"
          type="heading"
          text={translate('empty_state')}
          alignment="center"
          testId="choose-pickup-point-modal-empty-state"
        />
        <Spacer size="x2-large" />
      </Dialog>
    )
  }

  function renderModalContent() {
    if (availablePickupPoints?.nearbyShippingPoints.length === 0) return renderEmptyState()

    if (dataLoadingError || addressLoadingError)
      return <GenericErrorModal isShown onClose={handleClose} />

    if (isDataLoading || isAddressLoading)
      return (
        <Dialog show>
          <ContentLoader testId="choose-pickup-point-modal-loader" />
        </Dialog>
      )

    return (
      <div className="u-fill-width">
        <ScrollableArea>
          {renderNavigation()}
          {renderPikcupPointContent()}
          {renderSubmit()}
        </ScrollableArea>
      </div>
    )
  }

  return (
    <Dialog hasScrollableContent show={isOpen} testId="choose-pickup-point-modal">
      {renderModalContent()}
    </Dialog>
  )
}

export default AcceptChangeDeliveryOptionRequestModal
